console.log("index.js is loading...");

import { createRoot } from "react-dom/client";

import { CompanyDetailsContext, MatomoContext } from "@inbest/benefits-library";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";
import axios from "axios";
import { welfareAPIUrl } from "Constants/defaultValues";
import { getCompanyId, setCompanyCustomization } from "./constants/helpers";

console.log("All imports completed");

const rootEl = document.getElementById("root");
console.log("Root element:", rootEl);

const initSentry = () => {
  Sentry.init({
    dsn: "https://d8f8f0ca102eae5b2928db85e8631c3c@o4505924468604928.ingest.us.sentry.io/4505924471488512",
    environment: process.env.ENV,
    maskAllText: process.env.NODE_ENV === 'production' || process.env.ENV === 'production',
    integrations: [
      browserTracingIntegration({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/welfare\.inbest\.ai/,
        ],
      }),
      replayIntegration(),
    ],
    ignoreErrors: [/Network Error/i],
    beforeSend(event, hint) {
      // don't send not in prod environments
      if (process.env.ENV !== "sit" && process.env.ENV !== "production") {
        return null;
      }
      // Ignore errors from browser extensions
      if (
        hint &&
        hint.originalException &&
        hint.originalException.stack
      ) {
        const stack = hint.originalException.stack;
        if (
          stack.includes("chrome-extension://") ||
          stack.includes("safari-extension://")
        ) {
          return null;
        }
      }

      return event;
    },
    // Performance Monitoring
    tracesSampleRate: 0.02,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
};

let render = async () => {
  console.log("Starting render function...");
  const { default: MainApp } = await import("./App");
  console.log("MainApp imported successfully");
  const companyId = getCompanyId();
  console.log("Company ID:", companyId);

  const data = await setCompanyCustomization(companyId);
  let matomoTracker = null;
  if (data && !data.disableTracking) {
    initSentry();
    // Store the site ID for later use when Matomo loads
    if (data.matomoSiteId) {
      window._paq.push(["setSiteId", data.matomoSiteId]);
      window.matomoSiteId = data.matomoSiteId;

      // Only create tracker if Matomo is already loaded (from previous consent)
      if (window.Matomo) {
        try {
          matomoTracker = window.Matomo.getAsyncTracker();
        } catch (e) {
          // Fallback to creating a tracker if getAsyncTracker is not available
          matomoTracker = window.Matomo.getTracker(
            "https://webanalytics.inbest.ai/matomo.php",
            data.matomoSiteId
          );
          // Ensure the new tracker also requires cookie consent
          matomoTracker.requireCookieConsent();
        }

        // Check if consent was previously given and enable link tracking if so
        if (
          matomoTracker &&
          matomoTracker.getRememberedCookieConsent &&
          matomoTracker.getRememberedCookieConsent()
        ) {
          window._paq.push(["enableLinkTracking"]);
        }
      } else {
        console.log(
          "Matomo not loaded yet - will be loaded after cookie consent"
        );
      }
    } else {
      axios.post(welfareAPIUrl + "/utils/whitelabelLogging", {
        message: `No Matomo site ID configured`,
        severity: "warn",
      });
    }
  }
  console.log("Matomo tracker", matomoTracker);
  const gtag = window.gtag;
  if (gtag) {
    console.log("Seting dimension 1", companyId);
    gtag("set", "user_properties", { partnerName: companyId });
  }

  const root = createRoot(document.getElementById("root"));

  root.render(
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("companyId", companyId);
        scope.setContext("company", {
          companyId,
          partnerName: data?.partnerName,
        });
      }}
    >
      <CompanyDetailsContext.Provider value={data}>
        <MatomoContext.Provider value={matomoTracker}>
          <MainApp />
        </MatomoContext.Provider>
      </CompanyDetailsContext.Provider>
    </Sentry.ErrorBoundary>
  );
};

if (import.meta.hot) {
  import.meta.hot.accept("./App", () => {
    render();
  });
}

// Function to update Matomo tracker when it loads after initial render
window.updateMatomoTracker = function() {
  if (window.Matomo && window.matomoSiteId) {
    console.log("Updating Matomo tracker after dynamic load");
    try {
      const tracker = window.Matomo.getAsyncTracker();
      // Update the context if the app is already rendered
      if (window.matomoContextUpdate) {
        window.matomoContextUpdate(tracker);
      }
    } catch (e) {
      console.error("Failed to update Matomo tracker:", e);
    }
  }
};

// Override the onMatomoReady to also update tracker when loaded dynamically
const originalOnMatomoReady = window.onMatomoReady;
window.onMatomoReady = function() {
  if (originalOnMatomoReady) originalOnMatomoReady();
  window.updateMatomoTracker();
};

// Check if we have existing consent and Matomo is loaded, or just render
console.log("Checking matomoLoaded:", window.matomoLoaded);
if (window.matomoLoaded || window.matomoConfig?.isConsentGiven) {
  console.log("Matomo ready or consent given, rendering immediately");
  render();
} else {
  console.log("No consent yet, rendering app without waiting for Matomo");
  // Render immediately - Matomo will be loaded later if consent is given
  render();
}
console.log("index.js execution completed");
